import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import CyberLayout from '@/components/CyberLayout';
import { CheckCircle, Home, Copy } from 'lucide-react';
import { toast } from 'sonner';

export default function ThankYou() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [submissionId, setSubmissionId] = useState(null);

  useEffect(() => {
    const id = sessionStorage.getItem('submissionId');
    if (id) {
      setSubmissionId(id);
    }
    
    // Clear all session data
    sessionStorage.removeItem('privacyAccepted');
    sessionStorage.removeItem('privacyAcceptedAt');
    sessionStorage.removeItem('submissionId');
  }, []);

  const copyToClipboard = () => {
    if (submissionId) {
      navigator.clipboard.writeText(submissionId);
      toast.success('ID copiato!');
    }
  };

  return (
    <CyberLayout showLanguageSwitch={false}>
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-20">
        <div className="max-w-lg w-full text-center">
          {/* Success Icon */}
          <div className="relative mb-12 fade-in-up">
            <div className="w-32 h-32 mx-auto rounded-full bg-cyber-gray border-2 border-neon-green flex items-center justify-center neon-glow pulse-neon">
              <CheckCircle className="w-16 h-16 text-neon-green" />
            </div>
            <div className="absolute -inset-4 border border-neon-green/20 rounded-full mx-auto" style={{ width: 'calc(100% + 2rem)', left: '-1rem' }} />
          </div>

          {/* Title */}
          <h1 
            className="font-orbitron text-3xl sm:text-4xl font-bold text-white mb-4 tracking-wider neon-text fade-in-up"
            style={{ animationDelay: '0.1s' }}
            data-testid="thank-you-title"
          >
            {t('thankYouTitle')}
          </h1>
          
          <p 
            className="font-mono text-neon-cyan text-sm mb-10 fade-in-up"
            style={{ animationDelay: '0.2s' }}
          >
            {t('thankYouSubtitle')}
          </p>

          {/* Report ID */}
          {submissionId && (
            <div 
              className="cyber-card rounded-lg p-6 mb-10 fade-in-up"
              style={{ animationDelay: '0.3s' }}
            >
              <div className="font-mono text-xs text-neon-green/70 mb-2 uppercase tracking-wider">
                {t('reportId')}
              </div>
              <div className="flex items-center justify-center gap-3">
                <code className="font-mono text-lg text-white bg-cyber-surface px-4 py-2 rounded">
                  {submissionId.slice(0, 8)}...
                </code>
                <button
                  onClick={copyToClipboard}
                  className="p-2 hover:bg-neon-green/10 rounded transition-colors"
                  data-testid="copy-id-btn"
                >
                  <Copy className="w-5 h-5 text-neon-green" />
                </button>
              </div>
            </div>
          )}

          {/* Animation decoration */}
          <div className="mb-10 font-mono text-xs text-neon-green/50 fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="typing-effect inline-block">
              {'>'} TRANSMISSION_COMPLETE
            </div>
          </div>

          {/* Action */}
          <button
            onClick={() => navigate('/')}
            data-testid="back-home-btn"
            className="cyber-btn px-8 py-4 flex items-center justify-center gap-3 mx-auto glitch fade-in-up"
            style={{ animationDelay: '0.5s' }}
          >
            <Home className="w-5 h-5" />
            <span>{t('backToHome')}</span>
          </button>
        </div>
      </div>
    </CyberLayout>
  );
}
